import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MenuItem} from 'primeng/api';
import {forkJoin} from 'rxjs';
import {PageComponent} from 'features/core/shared/mvs-page/page.component';
import {NavigationItem} from 'features/core/shared/dto/navigation/navigation-item';
import {MvsCoreService} from 'features/core/shared/service/mvs-core.service';
import {PageHeaderConfig} from 'features/core/shared/service/page/page-header-config.interface';
import {QlService} from 'features/core/shared/ql/service/ql.service';
import {Sorting} from 'features/core/shared/misc/sorting';
import {FilterCriteria} from 'features/core/shared/filter/api/filter.criteria';
import {PagingDto} from 'features/core/shared/dto/model/paging.dto';
import {WidgetData} from 'features/core/shared/widget/widget-data';
import {WidgetFactory} from 'features/core/shared/widget/service/widget.factory';
import {
    CommissionProvisionTypeEnum
} from '../../model/protected-domain/enum/commission-provision-type/commission-provision-type.enum';
import {
    CommissionCalculationStatusEnum
} from '../../model/protected-domain/enum/commission-calculation-status/commission-calculation-status.enum';
import {CnReconciliationApiService} from '../../service/api/cn-reconciliation-api/cn-reconciliation-api.service';
import {CnCalculationApiService} from '../../service/api/cn-calculation/cn-calculation-api.service';
import {CnActivityTotalsDto} from '../../model/dto/api/cn-activity-totals.dto';
import {CnCalculationTotalsDto} from '../../model/dto/api/cn-calculation-totals.dto';
import {CnCalculationByPartnerDto} from '../../model/dto/api/cn-calculation-by-partner.dto';
import {CnActivityTotalsBucketDto} from '../../model/dto/api/cn-activity-totals-bucket.dto';
import {CnControllingKpiDto} from '../../model/dto/api/cn-controlling-kpi.dto';
import {CnActivityTrendDto} from '../../model/dto/api/cn-activity-trend.dto';
import {CnActivityTrendPointDto} from '../../model/dto/api/cn-activity-trend-point.dto';
import {CnCalculationTrendDto} from '../../model/dto/api/cn-calculation-trend.dto';
import {CnActivityByPartnerDto} from '../../model/dto/api/cn-activity-by-partner.dto';
import {CommissionActivityDto} from '../../model/dto/entity/commission-activity/commission-activity.dto';
import {CnActivityFilterDto} from '../../model/dto/api/cn-activity-filter.dto';
import {CnRecalcResultDto} from '../../model/dto/api/cn-recalc-result.dto';
import {cnErrorMessage} from '../../shared/cn-error-message.util';
import {downloadCsv} from '../../shared/cn-csv.util';
import {
    CommissionAcknowledgeStatusEnum
} from '../../model/protected-domain/enum/commission-acknowledge-status/commission-acknowledge-status.enum';
import {
    CommissionActivityKindEnum
} from '../../model/protected-domain/enum/commission-activity-kind/commission-activity-kind.enum';
import {FilterDate} from 'features/core/shared/enum/mvs-filter-date.enum';
import {
    FilterDateBetweenValues
} from 'features/core/shared/components/mvs-dashboard-duration-filter/mvs-dashboard-duration-filter.component';

/**
* Selectable option for the Gesellschaft (partner) dropdown — chosen by NAME, not raw id.
*/
interface CnPartnerOption {
    /** Partner (Gesellschaft) id used as the filter value. */
    id: number;
    /** Human-readable partner name shown in the dropdown. */
    name: string;
}

/**
 * Which provision-type bucket a Soll drill-down was opened for. Mirrors the columns/tiles of the
 * Soll view: AP (Abschlussprovision), BP (Bestandspflegeprovision), Sonstige (everything else) and
 * TOTAL (Gesamt — every provision type together, i.e. no provisionType scope).
 * Drives both the German title and the {@link CommissionProvisionTypeEnum} filter built in
 * {@link CnDashboardPage.buildCalculatedDrillDownWidget} (TOTAL → no provisionType filter).
 */
type CnSollDrillDownType = 'AP' | 'BP' | 'SONSTIGE' | 'TOTAL';

/**
* Provisions-Dashboard page (P3) for Sachbearbeitung / Controlling.
*
* WP-FE-1 — controlling cockpit on top of the operational inbox. Owns four screen areas, all
* scoped by the global Periode (YearMonth) + Gesellschaft (partner) filter:
*
*  1. Provisionssummen card — AP / BP / Gesamt EUR matrix split Offen vs Quittiert, with a prominent
*     "Zugeordnetes Provisionsgeschäft (AP + BP)" headline banner above the Sonstige/Gesamt rows so the
*     real AP+BP business leads (Jean-Marie). (getActivityTotals → GET /cn/activity/totals).
*  1b. Provisionen je Gesellschaft card — cross-Gesellschaft overview (Gesellschaft · AP · BP · AP+BP ·
*     Gesamt + Summen row), CSV-exportable (getActivityTotalsByPartner →
*     GET /cn/activity/totals/by-partner, Jean-Marie).
*  2. Controlling KPI bar — Quittierungs-Quote (%), Offen, Quittiert, Überfällig,
*     Heute quittiert, Ältester offener Vorgang — every KPI carries an (i) tooltip explaining the
*     figure in plain German (CnReconciliationApiService.getControllingKpis →
*     GET /cn/activity/controlling). The Gesamt value is taken from the backend total — never
*     recomputed in the FE.
*  3. Quittierungsprotokoll — the audit list of ACKNOWLEDGED activities
*     (CnReconciliationApiService.listActivities, status=ACKNOWLEDGED) showing Gesellschaft,
*     Art, Betrag, Quittiert von, Quittiert am, Notiz. Sorted by Quittiert am (acknowledgedAt) DESC and
*     audit-searchable by Quittiert von / Quittiert am von–bis (acknowledgedBy/From/To, Hans-Jürgen).
*  4. AP/BP-Trend — a compact last-N-months trend plus the "vs. Vormonat" delta in EUR and %, CSV-
*     exportable (CnReconciliationApiService.getTrend → GET /cn/activity/totals/trend).
*  4b. Datenqualität — the existing mvs-cn-data-quality-panel embedded as a cockpit card (Hans-Jürgen).
*  5. Operational inbox — hosted via mvs-cn-activity-inbox (FE-INBOX owns it); the dashboard passes
*     the global Periode + Gesellschaft into it via [period] / [partnerId] and re-loads
*     totals / controlling / protocol / trend live when the inbox emits (acknowledged).
*
* FE-3 / FE-4 / FE-5 / M10 / M11 (TM-1000001915): KPI tooltips + Quittierungs-Quote rename, CSV
* export (Provisionssummen / Controlling-KPIs / Quittierungsprotokoll) via cn-csv.util, the AP/BP
* trend card, a friendly empty state for future/empty periods, a "Stand:" timestamp and a manual
* refresh button.
*
* The Gesellschaft dropdown is populated via QL from the CONFIGURED commission partners only
* (cn.CommissionPartner joined N:1 to its cm.Partner relation, shown by partner.name) — a small set,
* so a plain p-select dropdown is used (not a load-all-partners search).
* NO calculation in the FE — all amounts / quotas / counters come from the backend.
*
* Header runs in TEMPLATE-Mode (sticky) and hosts the GLOBAL Zeitraum + Gesellschaft filter bar
* (Zeitraum picker · Gesellschaft · Filter zurücksetzen · Zeitraum/Stand indicator · Aktualisieren)
* so the filter is a recurring/global element across every left-nav section. The title bar carries
* three cross-links (Import / Eingangsfreigabe / Abgleich → their standalone cn routes, see
* {@link headerLinks}); these replace the formerly embedded import / importReview / reconciliation
* left-nav sections, while the remaining sections are still owned by the left-nav.
*/
@Component({
    selector: 'mvs-cn-dashboard-page',
    templateUrl: './cn-dashboard.page.html',
    styleUrls: ['./cn-dashboard.page.scss'],
    standalone: false
})
export class CnDashboardPage extends PageComponent implements OnInit {

    defaultLabel: string = 'Provisions-Dashboard';

    /**
     * Cross-links rendered in the title bar next to the "Provisions-Dashboard" heading
     * ({@link MvsConfigHeaderComponent} `[links]`), pointing to the standalone Import,
     * Eingangsfreigabe and Abgleich routes. The former embedded workflow sections (left-nav
     * import / importReview / reconciliation) were removed in favour of these header links.
     * `url` is the route path WITHOUT the leading `/#/` — MvsConfigHeaderComponent.openLink()
     * prepends it (hash routing), exactly as the standalone cn pages pass their cross-links.
     */
    readonly headerLinks: MenuItem[] = [
        {label: 'Import', icon: 'fa-light fa-file-import', url: 'cn/import'},
        {label: 'Eingangsfreigabe', icon: 'fa-light fa-clipboard-check', url: 'cn/import-review'},
        {label: 'Abgleich', icon: 'fa-light fa-scale-balanced', url: 'cn/reconciliation'}
    ];

    /** Expose the activity-kind enum to the template (Art Klartext mapping). */
    readonly ActivityKind = CommissionActivityKindEnum;

    // ===== Left navigation (workflow sections) =====
    /**
     * Currently active left-nav section (mirrors {@link activeNavigationItem}.action). The body
     * gates each workflow section with `@if (activeNavItem === '...')`; the GLOBAL Periode +
     * Gesellschaft filter bar stays rendered ABOVE the sections and drives the data in every one.
     */
    activeNavItem: string;

    /**
     * Left-navigation workflow sections, rendered by the app shell from {@link isNavComponent}.
     * Each maps the existing dashboard cards into one of six workflow steps (Übersicht → Soll → Ist
     * → Aktionskorb → Abgleich → Datenqualität). Exactly one default (Übersicht); the selection is
     * persisted in the URL as `?navItem=` (handled by PageComponent.subscribeToParams), so a page
     * refresh keeps the section. Labels are German with proper umlauts; icons are fa-solid.
     */
    navigationItems: NavigationItem[] = [
        {
            label: 'Übersicht',
            action: 'overview',
            tooltip: 'Controlling-Kennzahlen und Provisions-Trend',
            icon: 'fa-solid fa-gauge',
            toggleable: false,
            default: true
        },
        {
            label: 'Berechnete Provision (Soll)',
            action: 'calculated',
            tooltip: 'Berechnete erwartete Provision (Soll)',
            icon: 'fa-solid fa-calculator',
            toggleable: false,
            default: false
        },
        {
            label: 'Empfangene Provision (Ist)',
            action: 'received',
            tooltip: 'Tatsächlich empfangene/quittierte Provision (Ist)',
            icon: 'fa-solid fa-inbox',
            toggleable: false,
            default: false
        },
        {
            label: 'Aktionskorb',
            action: 'inbox',
            tooltip: 'Offene Provisions-Aktivitäten und Quittierungsprotokoll',
            icon: 'fa-solid fa-list-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Datenqualität',
            action: 'dataQuality',
            tooltip: 'Abdeckung und unvollständige Berechnungsbasis',
            icon: 'fa-solid fa-clipboard-check',
            toggleable: false,
            default: false
        },
        {
            label: 'Soll vs Ist (Verlauf)',
            action: 'sollIstTrend',
            tooltip: 'Monatlicher Verlauf: berechnete Provision (Soll) gegenüber empfangener/quittierter (Ist)',
            icon: 'fa-solid fa-chart-line',
            toggleable: false,
            default: false
        },
    ];

    // ===== Global filter (Zeitraum + Gesellschaft) =====
    /**
     * Selected period RANGE start as a YearMonth "YYYY-MM" string (inclusive), or null = alle Perioden.
     * Derived from the {@link MvsRangeSelectorOverlayComponent} startDate via {@link formatYearMonth}.
     * Sent to the dashboard aggregation endpoints as the `fromPeriod` query param.
     */
    filterFromPeriod: string | null = null;
    /**
     * Selected period RANGE end as a YearMonth "YYYY-MM" string (inclusive), or null = alle Perioden.
     * Derived from the range-selector endDate; sent as the `toPeriod` query param.
     */
    filterToPeriod: string | null = null;
    /**
     * Applied Zeitraum preset ({@link FilterDate}) — drives the "Stand"/active-filter description text
     * (e.g. "Dieser Monat"). CUSTOM / null fall back to the derived "MM.YYYY–MM.YYYY" range. Read lazily
     * by {@link activePeriodDescription} so the preset and the from/to range stay consistent regardless
     * of the order in which the overlay emits its label and value events.
     */
    filterPeriodPreset: FilterDate | null = null;
    /** Default Zeitraum preset shown on load — current month, so the dashboard looks the same as before. */
    readonly defaultFilterDate: FilterDate = FilterDate.THISMONTH;

    /** Selected Gesellschaft (partner) id; null = alle Gesellschaften. */
    filterPartnerId: number | null = null;

    // ===== Gesellschaft dropdown (CONFIGURED commission partners only, via QL) =====
    /**
     * Dropdown options for the Gesellschaft selector: the CONFIGURED commission partners only
     * (cn.CommissionPartner → cm.Partner), loaded once via QL and sorted by name. A leading
     * "Alle Gesellschaften" entry (id = null) is prepended in {@link buildPartnerOptions}.
     */
    partnerOptions: CnPartnerOption[] = [];
    /**
     * Currently selected Gesellschaft option (the resolved {id,name}); null = "Alle Gesellschaften".
     * Kept in sync with {@link filterPartnerId} (its id) on every change so the CSV/filter-description
     * text can show the real Gesellschaft name; the rest of the dashboard uses the plain partner-id filter.
     */
    selectedPartner: CnPartnerOption | null = null;
    /** Whether the Gesellschaft options are currently loading (drives the [loading] spinner). */
    partnerOptionsLoading: boolean = false;
    /** Last Gesellschaft-load error message, shown inline; null = no error. */
    partnerOptionsError: string | null = null;
    /** Maximum number of configured commission partners loaded (a small, configured set). */
    readonly partnerOptionsPageSize: number = 500;

    // ===== Provisionssummen Ist card (GET /cn/activity/totals) — Quittierung =====
    totals: CnActivityTotalsDto | null = null;
    totalsState: 'loading' | 'ready' | 'error' = 'loading';
    totalsError: string | null = null;

    // ===== Berechnete Provision (Soll) card (GET /cn/calculation/totals) — calculated commission =====
    calculatedTotals: CnCalculationTotalsDto | null = null;
    calculatedTotalsState: 'loading' | 'ready' | 'error' = 'loading';
    calculatedTotalsError: string | null = null;

    // ===== Berechnete Provision je Gesellschaft (Soll, GET /cn/calculation/totals/by-partner) =====
    /** Per-Gesellschaft Soll rows (AP / BP / Gesamt from CALCULATED records); shown in the Soll section. */
    calculatedByPartner: CnCalculationByPartnerDto[] = [];
    calculatedByPartnerState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
    calculatedByPartnerError: string | null = null;

    // ===== Soll drill-down (Overview → Detail, Fiori FlexibleColumnLayout) =====
    /**
     * Stable alias for the Soll drill-down detail table widget. Reused across every drill-down so the
     * widget identity (and any saved layout) is preserved while only its predefined filters change —
     * the same reuse pattern as the cn-config filtered-rule widget.
     */
    private static readonly DRILL_DOWN_ALIAS = 'cn.dashboard.calculation.drilldown.new';

    /**
     * Whether the Soll drill-down detail panel is open. When false the overview fills the full width
     * (detail collapsed); when true the splitter shrinks the overview (~40%) and shows the detail
     * table (~60%) on the right (Fiori overview→detail — the overview is not replaced, just smaller).
     */
    drillDownOpen: boolean = false;
    /** Panel sizes [overview, detail] for the Soll splitter — full overview when closed, 40/60 when open. */
    drillDownPanelSizes: number[] = [100, 0];
    /** German title of the open drill-down, e.g. "Verträge: Allianz · AP · Dieser Monat". */
    drillDownTitle: string = '';
    /** Partner (Gesellschaft) id the drill-down was opened for; null = none open. */
    private drillDownPartnerId: number | null = null;
    /** Provision-type bucket (AP / BP / Sonstige) the drill-down was opened for; null = none open. */
    private drillDownType: CnSollDrillDownType | null = null;
    /** The (re)built QL detail table widget on cn.CommissionCalculation, bound on the right panel. */
    drillDownWidgetData: WidgetData | null = null;

    // ===== Soll-Neuberechnung (POST /cn/commission-recalc/{partnerId}/recalculate) =====
    /** Whether a recalculation request is currently in flight (drives the button [loading]). */
    recalculating: boolean = false;
    /** Result/error message of the last recalculation, shown inline; null = nothing to show. */
    recalcMessage: string | null = null;
    /** Severity of {@link recalcMessage} for the inline p-message (success / info / error). */
    recalcMessageSeverity: 'success' | 'info' | 'error' = 'success';

    // ===== Controlling KPI bar (GET /cn/activity/controlling) =====
    controlling: CnControllingKpiDto | null = null;
    controllingState: 'loading' | 'ready' | 'error' = 'loading';
    controllingError: string | null = null;

    // ===== Provisionen je Gesellschaft (GET /cn/activity/totals/by-partner) — Jean-Marie cross-Gesellschaft =====
    byPartner: CnActivityByPartnerDto[] = [];
    byPartnerState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
    byPartnerError: string | null = null;

    // ===== Quittierungsprotokoll (audit list, status=ACKNOWLEDGED) =====
    protocolEntries: CommissionActivityDto[] = [];
    protocolState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
    protocolError: string | null = null;

    // ===== Quittierungsprotokoll audit filters (Hans-Jürgen) =====
    /** "Quittiert von" substring filter (acknowledgedBy) — null/empty = kein Filter. */
    protocolAckByFilter: string | null = null;
    /** "Quittiert am von" date (acknowledgedFrom, day start) — null = kein Filter. */
    protocolAckFromDate: Date | null = null;
    /** "Quittiert am bis" date (acknowledgedTo, day end) — null = kein Filter. */
    protocolAckToDate: Date | null = null;

    // ===== AP/BP-Trend (GET /cn/activity/totals/trend) — FE-5 =====
    /** Number of months requested for the trend series. */
    readonly trendMonths: number = 6;
    trend: CnActivityTrendDto | null = null;
    trendState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
    trendError: string | null = null;

    // ===== Soll vs Ist (Verlauf) line chart (GET /cn/calculation/trend + GET /cn/activity/totals/trend) =====
    /**
     * Combined load/empty/error state of the Soll-vs-Ist line chart. The chart fans out to TWO
     * backend trend endpoints (Soll = range-aware calculation/trend, Ist = months-window
     * activity/totals/trend) and is only 'ready' once BOTH resolved; 'empty' when neither carries a
     * single month. Lazy-loaded on entering the section and re-loaded on every range/Gesellschaft
     * change (see {@link refreshSollIstTrend}).
     */
    sollIstTrendState: 'loading' | 'ready' | 'empty' | 'error' = 'loading';
    sollIstTrendError: string | null = null;
    /** Whether the Soll-vs-Ist chart has been loaded at least once (drives the lazy first load). */
    private sollIstTrendLoaded: boolean = false;
    /** PrimeNG line-chart data for the Soll-vs-Ist Gesamt comparison ({ labels, datasets }); null until built. */
    sollIstChartData: any = null;
    /** Shared PrimeNG line-chart options (theme-token colours, EUR-friendly axes); built once in ngOnInit. */
    sollIstChartOptions: any = null;

    // ===== Aktualität (M11) =====
    /** Timestamp of the last successful dashboard refresh ("Stand: …"). */
    lastUpdated: Date | null = null;

    /**
     * Plain-German KPI tooltips (FE-3 / M4). Each controlling KPI gets an (i) tooltip explaining the
     * figure so the clerk understands the number before acting on it.
     */
    readonly kpiTooltips = {
        completionRate: 'Anteil der quittierten Vorgänge an allen Vorgängen (quittiert geteilt durch ' +
            'offen plus quittiert) im gewählten Zeitraum. Ohne Vorgänge wird ein Strich angezeigt.',
        open: 'Anzahl der noch nicht quittierten (offenen) Vorgänge im gewählten Zeitraum.',
        acknowledged: 'Anzahl der bereits quittierten Vorgänge im gewählten Zeitraum.',
        overdue: 'Offene Vorgänge, deren Buchungsperiode vor dem laufenden Monat liegt bzw. die seit ' +
            'zu langer Zeit offen sind. Diese sollten vorrangig bearbeitet werden.',
        throughputToday: 'Anzahl der heute quittierten Vorgänge (Tagesdurchsatz).',
        oldestOpen: 'Buchungsperiode des am längsten offenen Vorgangs. Die Klammer zeigt, seit wie ' +
            'vielen Tagen dieser Vorgang bereits offen ist.'
    };

    constructor(
        protected override route: ActivatedRoute,
        protected override coreService: MvsCoreService,
        private reconciliationApiService: CnReconciliationApiService,
        private calculationApiService: CnCalculationApiService,
        private qlService: QlService
    ) {
        super(route, coreService);
    }

    ngOnInit(): void {
        this.defaultLabel = 'Provisions-Dashboard';
        super.ngOnInit();
        // Seed the default Zeitraum (current month) BEFORE the first refresh so the initial load already
        // scopes to the current month (same as the former single-month picker default). The range-selector
        // child re-confirms the same range on its own init (idempotent), keeping the picker label in sync.
        this.applyDefaultPeriodRange();
        // Build the Soll-vs-Ist chart options once (theme-token colours / axes); the chart DATA is
        // (lazy-)loaded only when the user opens the "Soll vs Ist (Verlauf)" section.
        this.sollIstChartOptions = this.buildSollIstChartOptions();
        // Load the Gesellschaft dropdown options once (CONFIGURED commission partners only, via QL).
        this.loadPartnerOptions();
        this.refreshDashboard();
        this.initialized = true;
    }

    /**
     * Seed {@link filterFromPeriod} / {@link filterToPeriod} / {@link filterPeriodPreset} from the default
     * "Dieser Monat" preset so the dashboard loads scoped to the current month. Uses the current month
     * for both the inclusive range start and end.
     */
    private applyDefaultPeriodRange(): void {
        const now = new Date();
        const period = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
        this.filterFromPeriod = period;
        this.filterToPeriod = period;
        this.filterPeriodPreset = this.defaultFilterDate;
    }

    /**
     * Render the page header in TEMPLATE mode from the page's own <ng-template #headerTemplate>.
     *
     * The header now hosts the GLOBAL filter bar (Zeitraum + Gesellschaft + Filter zurücksetzen +
     * the Zeitraum/Stand indicator + Aktualisieren), so the filter is a recurring/global element that
     * stays visible across every left-nav section. `sticky` keeps it pinned below the topbar while the
     * section body scrolls. The former MvsConfigHeaderComponent cross-link buttons (Abgleich /
     * Konfiguration) were removed — the left-nav owns section switching.
     *
     * Timing: `@ViewChild('headerTemplate', {static: true})` (declared on PageComponent) resolves
     * BEFORE super.ngOnInit() runs setActivePage(), which is what triggers MvsPageHeaderComponent to
     * pull this config — so `this.headerTemplate` is already populated here.
     */
    getPageHeaderConfig(): PageHeaderConfig {
        return {
            templateRef: this.headerTemplate,
            sticky: true
        };
    }

    /**
    * Load the Gesellschaft dropdown options: the CONFIGURED commission partners only.
    *
    * The selectable set is small (one cn.CommissionPartner per Gesellschaft), so we load it eagerly
    * via a single QL query and render a plain p-select dropdown (no load-all-partners search). The
    * query starts at cn.CommissionPartner and joins N:1 to its `partner` relation (cm.Partner),
    * selecting/sorting the joined partner's id + name — so the dropdown shows the real Gesellschaft
    * NAME. Rows are de-duplicated by partner id (defensive; there is one CommissionPartner per partner)
    * and sorted by name. The resolved {@link buildPartnerOptions} prepends the "Alle Gesellschaften"
    * (null) default entry.
    */
    loadPartnerOptions(): void {
        this.partnerOptionsLoading = true;
        this.partnerOptionsError = null;

        this.qlService.builder()
            .query({
                name: 'cnPartners',
                type: 'view',
                start: {
                    type: 'entity',
                    name: 'cn.CommissionPartner',
                    as: 'e',
                    // N:1 join to the `partner` relation (cm.Partner); the joined partner's id + name are
                    // read off the 'p' alias (p.id / p.name), so the dropdown shows the real Gesellschaft name.
                    joins: [
                        {
                            joinType: 'left',
                            type: 'entity',
                            name: 'partner',
                            as: 'p'
                        }
                    ]
                },
                sortings: [
                    new Sorting('p.name', true)
                ],
                paging: PagingDto.createSize(this.partnerOptionsPageSize)
            })
            .execute()
            .subscribe({
                next: (result) => {
                    const rows: any[] = result.get('cnPartners') ?? [];
                    this.partnerOptions = this.buildPartnerOptions(rows);
                    this.partnerOptionsLoading = false;
                },
                error: (err) => {
                    this.partnerOptions = this.buildPartnerOptions([]);
                    this.partnerOptionsLoading = false;
                    this.partnerOptionsError = err?.message || 'Die Gesellschaften konnten nicht geladen werden.';
                }
            });
    }

    /**
    * Map the joined cn.CommissionPartner → cm.Partner QL rows to dropdown options: de-duplicate by
    * partner id, map to {id, name} (falling back to "Gesellschaft #id" for a missing name), keep the
    * backend name order, and prepend the "Alle Gesellschaften" (id = null) default entry.
    */
    private buildPartnerOptions(rows: any[]): CnPartnerOption[] {
        const seen = new Set<number>();
        const options: CnPartnerOption[] = [];
        for (const r of rows) {
            const id: number = r['p.id'];
            if (id == null || seen.has(id)) {
                continue;
            }
            seen.add(id);
            options.push({id, name: r['p.name'] ?? `Gesellschaft #${id}`});
        }
        // Leading "Alle Gesellschaften" entry (null id) = no Gesellschaft scope (the default).
        return [{id: null as unknown as number, name: 'Alle Gesellschaften'}, ...options];
    }

    /**
    * Apply a Gesellschaft selection from the p-select dropdown: set {@link filterPartnerId} (null for
    * "Alle Gesellschaften"), keep {@link selectedPartner} in sync (for the CSV/filter-description name),
    * and refresh the dashboard. Preserves the existing filter -> refresh wiring (via onFilterChange).
    *
    * @param partnerId the selected partner id (the dropdown's optionValue), or null for "Alle Gesellschaften"
    */
    onPartnerChange(partnerId: number | null): void {
        this.filterPartnerId = partnerId ?? null;
        this.selectedPartner = partnerId != null
            ? (this.partnerOptions.find(o => o.id === partnerId) ?? null)
            : null;
        this.onFilterChange();
    }

    /**
    * Re-run all backend-scoped sections (totals, controlling KPIs, protocol, trend) with the current
    * Periode + Gesellschaft filter. Triggered on init, on every filter change and on manual refresh.
    * Stamps the "Stand:" timestamp (M11).
    */
    refreshDashboard(): void {
        this.refreshTotals();
        this.refreshCalculatedTotals();
        this.refreshCalculatedByPartner();
        this.refreshByPartner();
        this.refreshControlling();
        this.refreshProtocol();
        this.refreshTrend();
        // Re-load the Soll-vs-Ist chart on every range/Gesellschaft change — but only once it has
        // been opened at least once, so the two extra trend calls stay lazy until the user needs them.
        if (this.sollIstTrendLoaded) {
            this.refreshSollIstTrend();
        }
        this.lastUpdated = new Date();
    }

    /**
    * Sync the active left-nav section from the URL `?navItem=` selection.
    *
    * Called by PageComponent.subscribeToParams() after it resolves {@link activeNavigationItem}
    * (the `?navItem=` value, or the `default: true` item — Übersicht — when absent). The body gates
    * each workflow section on {@link activeNavItem}; switching sections does NOT re-fetch — the data
    * is already loaded once via the global filter, so section changes are instant.
    */
    override postNavigationSelection(): void {
        if (this.activeNavigationItem) {
            this.activeNavItem = this.activeNavigationItem.action;
            // The other sections are pre-loaded once via the global filter, so a section switch is
            // instant. The Soll-vs-Ist line chart is the only LAZY section: it fans out to two extra
            // trend endpoints, so we load it the first time the user opens it (and thereafter let the
            // global filter re-load it — see refreshDashboard()).
            if (this.activeNavItem === 'sollIstTrend' && !this.sollIstTrendLoaded) {
                this.refreshSollIstTrend();
            }
        }
    }

    /** Provide the left-navigation workflow sections to the app shell. */
    override isNavComponent(): NavigationItem[] {
        return this.navigationItems;
    }

    /**
    * Live cockpit refresh after the inbox acknowledges (FE-1). Bound to the inbox (acknowledged)
    * output: reloads the totals, controlling KPIs and protocol so the single-source controlling bar
    * reflects the new state immediately. The trend is period-aggregated and acknowledging does not
    * change the EUR totals, so it is intentionally left untouched here.
    */
    reloadTotalsControllingProtocol(): void {
        this.refreshTotals();
        this.refreshByPartner();
        this.refreshControlling();
        this.refreshProtocol();
        this.lastUpdated = new Date();
    }

    /** Apply a changed filter value: re-fetch the backend-scoped sections. */
    onFilterChange(): void {
        // A changed Periode / Gesellschaft changes the Soll population, so any open drill-down (built for
        // the previous scope) is collapsed back to the full-width overview to avoid a stale detail table.
        this.closeCalculatedDrillDown();
        this.refreshDashboard();
    }

    /**
     * Apply a new Zeitraum (range) selection from the {@link MvsRangeSelectorOverlayComponent}.
     *
     * The overlay emits a {startDate, endDate} pair (YYYY-MM-DD strings in localDate mode). We take the
     * year-month of each end ({@link formatYearMonth}) as the inclusive fromPeriod / toPeriod ("YYYY-MM")
     * sent to the dashboard aggregation endpoints, then refresh the whole dashboard. A cleared range
     * resets back to alle Perioden.
     */
    onPeriodRangeChange(values: FilterDateBetweenValues | null): void {
        const from = this.formatYearMonth(values?.startDate);
        const to = this.formatYearMonth(values?.endDate);
        // Skip the redundant refresh when the range-selector re-emits the already-applied default range
        // on its own init (the page seeded the same current-month range in ngOnInit).
        if (from === this.filterFromPeriod && to === this.filterToPeriod) {
            return;
        }
        this.filterFromPeriod = from;
        this.filterToPeriod = to;
        // The Zeitraum drives the drill-down's bookingMonth filter, so collapse any open detail panel.
        this.closeCalculatedDrillDown();
        this.refreshDashboard();
    }

    /**
     * Capture the applied Zeitraum preset ({@link FilterDate}) for the "Stand"/active-filter description.
     * Bound to the range-selector overlay (filterDateChange), which emits the applied preset (or null
     * when cleared). The displayed text is computed lazily in {@link activePeriodDescription}.
     */
    onPeriodLabelChange(filterDate: FilterDate | null): void {
        this.filterPeriodPreset = filterDate ?? null;
    }

    /** German preset labels mirrored from the range-selector — used for the Stand/filter description. */
    private readonly filterDateLabels: Record<FilterDate, string> = {
        [FilterDate.TODAY]: 'Heute',
        [FilterDate.YESTERDAY]: 'Gestern',
        [FilterDate.THISWEEK]: 'Letzte 7 Tage',
        [FilterDate.CALENDAR_WEEK]: 'Aktuelle Kalenderwoche',
        [FilterDate.THISMONTH]: 'Dieser Monat',
        [FilterDate.LASTMONTH]: 'Letzter Monat',
        [FilterDate.THISYEAR]: 'Dieses Jahr',
        [FilterDate.LASTYEAR]: 'Letztes Jahr',
        [FilterDate.CUSTOM]: 'Benutzerdefinierter Zeitraum'
    };

    /** Reset the global filter back to alle Perioden / alle Gesellschaften. */
    resetFilter(): void {
        this.filterFromPeriod = null;
        this.filterToPeriod = null;
        this.filterPeriodPreset = null;
        this.filterPartnerId = null;
        this.selectedPartner = null;
        this.closeCalculatedDrillDown();
        this.refreshDashboard();
    }

    /** Whether any global filter is active (drives the reset button visibility). */
    hasActiveFilter(): boolean {
        return this.filterFromPeriod != null || this.filterToPeriod != null || this.filterPartnerId != null;
    }

    /**
    * Load the Provisionssummen matrix (AP / BP / Sonstige / Gesamt × Offen / Quittiert).
    */
    refreshTotals(): void {
        this.totalsState = 'loading';
        this.totalsError = null;

        this.reconciliationApiService.getActivityTotals(
            this.resolvePeriod(), this.resolvePartnerId(), this.resolveFromPeriod(), this.resolveToPeriod()
        ).subscribe({
            next: (value) => {
                this.totals = value ?? null;
                this.totalsState = 'ready';
            },
            error: (err) => {
                this.totalsState = 'error';
                this.totalsError = err?.message || 'Die Provisionssummen konnten nicht geladen werden.';
            }
        });
    }

    /**
    * Load the BERECHNETE Provision (Soll) totals — AP / BP / Sonstige / Gesamt EUR sums from the
    * CALCULATED commission records (GET /cn/calculation/totals), scoped by the SAME global Periode +
    * Gesellschaft filter as the rest of the dashboard. No period -> all months. NO calculation in
    * the FE — the four bucket totals come straight from the backend.
    */
    refreshCalculatedTotals(): void {
        this.calculatedTotalsState = 'loading';
        this.calculatedTotalsError = null;

        this.calculationApiService.getTotals(
            this.resolvePeriod(), this.resolvePartnerId(), this.resolveFromPeriod(), this.resolveToPeriod()
        ).subscribe({
            next: (value) => {
                this.calculatedTotals = value ?? null;
                this.calculatedTotalsState = 'ready';
            },
            error: (err) => {
                this.calculatedTotalsState = 'error';
                this.calculatedTotalsError = err?.message || 'Die berechnete Provision (Soll) konnte nicht geladen werden.';
            }
        });
    }

    /**
    * Load the per-Gesellschaft BERECHNETE Provision (Soll) rows (AP / BP / Gesamt from CALCULATED
    * records, GET /cn/calculation/totals/by-partner), scoped by the SAME global Periode +
    * Gesellschaft filter. Backend-ordered by partnerName. NO calculation in the FE — the per-row
    * AP / BP / Gesamt come from the backend; only AP+BP is a plain additive aggregation of two
    * backend buckets (see {@link calcApBp}).
    */
    refreshCalculatedByPartner(): void {
        this.calculatedByPartnerState = 'loading';
        this.calculatedByPartnerError = null;

        this.calculationApiService.getTotalsByPartner(
            this.resolvePeriod(), this.resolvePartnerId(), this.resolveFromPeriod(), this.resolveToPeriod()
        ).subscribe({
            next: (rows) => {
                this.calculatedByPartner = rows ?? [];
                this.calculatedByPartnerState = this.calculatedByPartner.length ? 'ready' : 'empty';
            },
            error: (err) => {
                this.calculatedByPartnerState = 'error';
                this.calculatedByPartnerError = err?.message
                    || 'Die berechnete Provision je Gesellschaft (Soll) konnte nicht geladen werden.';
            }
        });
    }

    /** AP + BP of a Soll-by-Gesellschaft row — plain additive aggregation of two backend buckets. */
    calcApBp(row: CnCalculationByPartnerDto): number {
        return (row.ap ?? 0) + (row.bp ?? 0);
    }

    /** Column total of the Soll-by-Gesellschaft rows for the Summen line. */
    calculatedByPartnerSum(field: 'ap' | 'bp' | 'gesamt'): number {
        return this.calculatedByPartner.reduce((acc, r) => acc + (r[field] ?? 0), 0);
    }

    /** AP + BP column total of the Soll-by-Gesellschaft rows for the Summen line. */
    calculatedByPartnerApBpSum(): number {
        return this.calculatedByPartnerSum('ap') + this.calculatedByPartnerSum('bp');
    }

    // =====================================================================================
    // Soll drill-down (Overview → Detail) — clickable AP / BP / Sonstige → contract table
    // =====================================================================================

    /**
     * Open the Soll drill-down (Fiori overview→detail) for one Gesellschaft × Provisionsart.
     *
     * Captures {@link drillDownPartnerId} / {@link drillDownType} / a German title and (re)builds the
     * detail QL table widget on cn.CommissionCalculation with predefined filters (partnerId +
     * provisionType + the dashboard's booking-month range). Shows the right detail panel and shrinks
     * the overview to ~40 % via {@link drillDownPanelSizes}.
     *
     * Bound to the clickable AP / BP / Sonstige cells of the "Provisionen je Gesellschaft (Soll)" table
     * and to the AP / BP / Sonstige summary tiles when a single Gesellschaft is selected.
     *
     * @param partnerId   the Gesellschaft id whose calculations to show
     * @param partnerName the Gesellschaft name for the title (null → "Gesellschaft #id")
     * @param type        which provision-type bucket was clicked (AP / BP / Sonstige)
     */
    openCalculatedDrillDown(partnerId: number, partnerName: string | null, type: CnSollDrillDownType): void {
        if (partnerId == null) {
            return;
        }
        this.drillDownPartnerId = partnerId;
        this.drillDownType = type;
        const name = partnerName ?? `Gesellschaft #${partnerId}`;
        this.drillDownTitle = `Verträge: ${name} · ${this.drillDownTypeLabel(type)} · ${this.activePeriodDescription()}`;
        this.buildCalculatedDrillDownWidget();
        this.drillDownOpen = true;
        // Overview shrinks to ~40 %, detail table takes ~60 % on the right (Fiori overview→detail).
        this.drillDownPanelSizes = [40, 60];
    }

    /**
     * Collapse the Soll drill-down back to a full-width overview (× close button). Drops the captured
     * selection and the built widget so re-opening rebuilds it fresh against the current filter.
     */
    closeCalculatedDrillDown(): void {
        this.drillDownOpen = false;
        this.drillDownPanelSizes = [100, 0];
        this.drillDownPartnerId = null;
        this.drillDownType = null;
        this.drillDownWidgetData = null;
        this.drillDownTitle = '';
    }

    /** Short German label for a drill-down provision-type bucket (title + nothing else). */
    private drillDownTypeLabel(type: CnSollDrillDownType): string {
        switch (type) {
            case 'AP':
                return 'AP';
            case 'BP':
                return 'BP';
            case 'TOTAL':
                return 'Gesamt';
            default:
                return 'Sonstige';
        }
    }

    /**
     * (Re)build the Soll drill-down detail table widget on cn.CommissionCalculation under the stable
     * {@link CnDashboardPage.DRILL_DOWN_ALIAS}, with predefined filters derived from the click and the
     * dashboard's global filter:
     *   - partnerId   EQ  the clicked Gesellschaft (hard scope).
     *   - provisionType  AP → EQ ACQUISITION(0); BP → EQ PORTFOLIO_CARE(6);
     *                    Sonstige → IN over the remaining ordinals (1..5);
     *                    TOTAL → no provisionType scope (alle Provisionsarten — AP, BP & Sonstige rows together).
     *   - bookingMonth   BETWEEN fromPeriod..toPeriod when both are set (the dashboard's Zeitraum),
     *                    EQ a single month when from === to, otherwise unscoped (alle Perioden).
     *   - status      EQ ACTIVE — only the live calculation rows (SUPERSEDED are audit-only).
     *
     * The QL table resolves FK texts (commissionModelId → model name) like the cn-config rule widget.
     * NO calculation in the FE — the widget renders the raw backend rows; only filters are assembled.
     */
    private buildCalculatedDrillDownWidget(): void {
        if (this.drillDownPartnerId == null || this.drillDownType == null) {
            return;
        }

        const provisionTypeFilter = this.buildProvisionTypeFilter(this.drillDownType);
        const filters: FilterCriteria[] = [
            FilterCriteria.create('e.partnerId', FilterCriteria.cOperatorEqual, this.drillDownPartnerId),
            // Only ACTIVE calculation rows — SUPERSEDED rows are kept for audit and must not be summed/shown.
            // status is @Enumerated(ORDINAL) (SMALLINT), so bind the ORDINAL NUMBER (ACTIVE = 0), NOT the
            // name string 'ACTIVE' — a string bind makes Postgres throw "operator does not exist: smallint = text".
            FilterCriteria.create('e.status', FilterCriteria.cOperatorEqual, CommissionCalculationStatusEnum.ACTIVE)
        ];

        // TOTAL drills into ALL provision types → no provisionType scope (filter is null); AP/BP/Sonstige scope by type.
        if (provisionTypeFilter) {
            filters.push(provisionTypeFilter);
        }

        const periodFilter = this.buildBookingMonthFilter();
        if (periodFilter) {
            filters.push(periodFilter);
        }

        this.drillDownWidgetData = WidgetFactory.createWidgetTableEntityFkeyResolveQl(
            CnDashboardPage.DRILL_DOWN_ALIAS,
            this.drillDownTitle,
            'cn.CommissionCalculation',
            filters,
            // Newest booking month first, then contract — a stable, readable order for the detail table.
            [new Sorting('e.bookingMonth', false), new Sorting('e.contractId', true)]
        );
    }

    /**
     * Build the provisionType FilterCriteria for a drill-down bucket: AP → EQ ACQUISITION, BP → EQ
     * PORTFOLIO_CARE, Sonstige → IN over every remaining {@link CommissionProvisionTypeEnum} ordinal
     * (i.e. all types that are neither AP nor BP). Ordinals are the persisted enum values.
     *
     * Returns null for TOTAL (Gesamt) → no provisionType scope, so the drill-down shows ALL provision
     * types (AP, BP and Sonstige rows together) for the Gesellschaft + Zeitraum.
     */
    private buildProvisionTypeFilter(type: CnSollDrillDownType): FilterCriteria | null {
        if (type === 'TOTAL') {
            return null;
        }
        if (type === 'AP') {
            return FilterCriteria.create(
                'e.provisionType', FilterCriteria.cOperatorEqual, CommissionProvisionTypeEnum.ACQUISITION);
        }
        if (type === 'BP') {
            return FilterCriteria.create(
                'e.provisionType', FilterCriteria.cOperatorEqual, CommissionProvisionTypeEnum.PORTFOLIO_CARE);
        }
        // Sonstige = every provision type that is neither AP (ACQUISITION) nor BP (PORTFOLIO_CARE).
        const sonstigeOrdinals = [
            CommissionProvisionTypeEnum.COLLECTION,
            CommissionProvisionTypeEnum.RENEWAL,
            CommissionProvisionTypeEnum.LEADERSHIP,
            CommissionProvisionTypeEnum.OTHER,
            CommissionProvisionTypeEnum.BROKERAGE
        ];
        return FilterCriteria.create('e.provisionType', FilterCriteria.cOperatorIn, sonstigeOrdinals);
    }

    /**
     * Build the bookingMonth FilterCriteria from the dashboard's Zeitraum: BETWEEN fromPeriod..toPeriod
     * for a real range, EQ a single month when from === to, or null (no booking-month scope) when no
     * Zeitraum is set. The YearMonth column is stored as "YYYY-MM" so the string bounds compare
     * chronologically.
     */
    private buildBookingMonthFilter(): FilterCriteria | null {
        const from = this.filterFromPeriod;
        const to = this.filterToPeriod;
        if (!from || !to) {
            return null;
        }
        if (from === to) {
            return FilterCriteria.create('e.bookingMonth', FilterCriteria.cOperatorEqual, from);
        }
        return FilterCriteria.create('e.bookingMonth', FilterCriteria.cOperatorBetween, from, to);
    }

    /**
    * Whether the Soll-Neuberechnung can be triggered. Requires a selected Gesellschaft — a full
    * recalc-all is heavy, so the button is disabled (with a hint tooltip) for "Alle Gesellschaften"
    * to avoid triggering it accidentally — and no request must already be in flight.
    */
    canRecalculate(): boolean {
        return this.filterPartnerId != null && !this.recalculating;
    }

    /**
    * Trigger the idempotent Soll-Neuberechnung for the selected Gesellschaft
    * (POST /cn/commission-recalc/{partnerId}/recalculate). The button is only enabled when a
    * Gesellschaft is selected (see {@link canRecalculate}); we re-check here to be safe.
    *
    * On a synchronous run the backend returns the final counters → we show a German result summary
    * and refresh the Soll card so it reflects the new figures. For a large partner the run is
    * dispatched asynchronously (status "RUNNING") → we inform the user it runs in the background and
    * still refresh the Soll card (the new figures appear once the run completes; the user can also
    * use "Aktualisieren"). Errors surface a calm German message via {@link cnErrorMessage}. NO
    * calculation in the FE — every counter comes verbatim from the backend.
    */
    recalculate(): void {
        if (!this.canRecalculate() || this.filterPartnerId == null) {
            return;
        }

        this.recalculating = true;
        this.recalcMessage = null;

        this.calculationApiService.recalculate(this.filterPartnerId).subscribe({
            next: (result: CnRecalcResultDto) => {
                if (result?.status === 'RUNNING') {
                    this.recalcMessageSeverity = 'info';
                    this.recalcMessage = `Berechnung gestartet (Lauf ${result.runId}) … Sie läuft im ` +
                        `Hintergrund. Die berechnete Provision (Soll) wird nach Abschluss aktualisiert.`;
                } else {
                    this.recalcMessageSeverity = 'success';
                    this.recalcMessage = `Neu berechnet: ${result?.insertedCount ?? 0} neu, ` +
                        `${result?.updatedCount ?? 0} geändert, ${result?.deletedCount ?? 0} entfernt, ` +
                        `${result?.unchangedCount ?? 0} unverändert.`;
                }
                // Refresh the Soll card so it reflects the recalculated figures (synchronous run) or
                // picks them up once the background run completes (async run).
                this.refreshCalculatedTotals();
                this.refreshCalculatedByPartner();
                this.lastUpdated = new Date();
                this.recalculating = false;
            },
            error: (err) => {
                this.recalcMessageSeverity = 'error';
                this.recalcMessage = cnErrorMessage(err);
                this.recalculating = false;
            }
        });
    }

    /**
    * Load the cross-Gesellschaft overview (Provisionen je Gesellschaft) — one row per partner with
    * activity (AP / BP / AP+BP / Gesamt), backend-ordered by partnerName. Jean-Marie MAJOR.
    */
    refreshByPartner(): void {
        this.byPartnerState = 'loading';
        this.byPartnerError = null;

        this.reconciliationApiService.getActivityTotalsByPartner(
            this.resolvePeriod(), this.resolvePartnerId(), this.resolveFromPeriod(), this.resolveToPeriod()
        ).subscribe({
            next: (rows) => {
                this.byPartner = rows ?? [];
                this.byPartnerState = this.byPartner.length ? 'ready' : 'empty';
            },
            error: (err) => {
                this.byPartnerState = 'error';
                this.byPartnerError = err?.message || 'Die Provisionen je Gesellschaft konnten nicht geladen werden.';
            }
        });
    }

    /**
    * Load the controlling KPI bar values.
    */
    refreshControlling(): void {
        this.controllingState = 'loading';
        this.controllingError = null;

        this.reconciliationApiService.getControllingKpis(
            this.resolvePeriod(), this.resolvePartnerId(), this.resolveFromPeriod(), this.resolveToPeriod()
        ).subscribe({
            next: (value) => {
                this.controlling = value ?? null;
                this.controllingState = 'ready';
            },
            error: (err) => {
                this.controllingState = 'error';
                this.controllingError = err?.message || 'Die Controlling-Kennzahlen konnten nicht geladen werden.';
            }
        });
    }

    /**
    * The displayed trend series with leading all-zero months trimmed away (Jean-Marie #7, honest axis).
    *
    * The backend always returns a fixed {@link trendMonths}-month window, so the leading months are
    * 0,00 € until data exists. Showing those implies "N months of data" when only the tail is populated.
    * We therefore start the displayed series at the first month that carries any AP / BP / Gesamt value;
    * a genuinely all-zero series falls back to the full window (so the table is never empty). This is a
    * pure presentation trim — no EUR figure is invented or recomputed.
    */
    trendDisplaySeries(): CnActivityTrendPointDto[] {
        const series = this.trend?.series ?? [];
        const firstPopulated = series.findIndex(p => this.isPopulatedTrendPoint(p));
        return firstPopulated > 0 ? series.slice(firstPopulated) : series;
    }

    /**
    * Honest subtitle for the trend card (Jean-Marie #7). When the populated range is shorter than the
    * requested window, state the actual first populated period ("Daten ab YYYY-MM") instead of implying
    * the full "(letzte N Monate)" window is filled. Falls back to the plain N-months wording when the
    * whole window is populated (or empty).
    */
    trendRangeSubtitle(): string {
        const fullSeries = this.trend?.series ?? [];
        const displayed = this.trendDisplaySeries();
        if (displayed.length > 0 && displayed.length < fullSeries.length) {
            return `Daten ab ${displayed[0].period}`;
        }
        return `letzte ${this.trendMonths} Monate`;
    }

    /** Whether a trend point carries any non-zero AP / BP / Gesamt amount. */
    private isPopulatedTrendPoint(point: CnActivityTrendPointDto): boolean {
        return (point.ap ?? 0) !== 0 || (point.bp ?? 0) !== 0 || (point.gesamt ?? 0) !== 0;
    }

    /**
    * Load the compact AP/BP trend series + "vs. Vormonat" delta (FE-5). Scoped by the global
    * Gesellschaft (Jean-Marie #7): the trend re-fetches on every global filter change via
    * {@link onFilterChange} → {@link refreshDashboard}, passing {@link resolvePartnerId} so it reflects
    * the selected Gesellschaft ("Alle Gesellschaften" → undefined → all-partners trend). The trend is
    * inherently period-spanning, so the Periode filter does NOT narrow it.
    */
    refreshTrend(): void {
        this.trendState = 'loading';
        this.trendError = null;

        this.reconciliationApiService.getTrend(this.trendMonths, this.resolvePartnerId()).subscribe({
            next: (value) => {
                this.trend = value ?? null;
                this.trendState = (value && value.series && value.series.length > 0) ? 'ready' : 'empty';
            },
            error: (err) => {
                this.trendState = 'error';
                this.trendError = err?.message || 'Der Provisions-Trend konnte nicht geladen werden.';
            }
        });
    }

    // =====================================================================================
    // Soll vs Ist (Verlauf) — monthly Soll-vs-Ist line chart
    // =====================================================================================

    /**
    * Load the "Soll vs Ist (Verlauf)" line chart: the monthly BERECHNETE Provision (Soll) Gesamt
    * against the empfangene/quittierte Ist Gesamt over the selected Zeitraum.
    *
    * Two backend trend endpoints are fanned out via forkJoin and aligned by month label:
    *  - Soll: GET /cn/calculation/trend?fromPeriod&toPeriod&partnerId — range-aware + zero-filled,
    *          so it already spans exactly the selected Zeitraum (points[].month / .gesamt).
    *  - Ist:  GET /cn/activity/totals/trend?months=N&partnerId — a months-WINDOW ending at the
    *          current month (it has no from/to facet). N is derived from the selected range
    *          ({@link rangeMonthCount}, default 12). Series points are keyed by period (YYYY-MM).
    *
    * The two series are merged on the union of their YYYY-MM month labels (sorted ascending); a
    * month present in one series but not the other contributes 0 to the missing line, exactly as the
    * spec requires. NO calculation in the FE on the EUR figures — only month-keyed re-indexing and the
    * zero-fill of absent months.
    *
    * Known limitation (surfaced in the section caption): the Ist series is a months-window ending at
    * the CURRENT month, so a range fully in the past may not align perfectly with the Soll range — the
    * union-of-labels merge keeps the chart honest (missing Ist months simply read 0) and is acceptable
    * for v1.
    */
    refreshSollIstTrend(): void {
        this.sollIstTrendLoaded = true;
        this.sollIstTrendState = 'loading';
        this.sollIstTrendError = null;

        const fromPeriod = this.resolveFromPeriod();
        const toPeriod = this.resolveToPeriod();
        const partnerId = this.resolvePartnerId();
        const months = this.rangeMonthCount();

        forkJoin({
            soll: this.calculationApiService.getCalculationTrend(fromPeriod, toPeriod, partnerId),
            ist: this.reconciliationApiService.getTrend(months, partnerId)
        }).subscribe({
            next: ({soll, ist}) => {
                const sollPoints = soll?.points ?? [];
                const istPoints = ist?.series ?? [];
                this.sollIstChartData = this.buildSollIstChartData(sollPoints, istPoints);
                const hasData = sollPoints.length > 0 || istPoints.length > 0;
                this.sollIstTrendState = hasData ? 'ready' : 'empty';
            },
            error: (err) => {
                this.sollIstChartData = null;
                this.sollIstTrendState = 'error';
                this.sollIstTrendError = err?.message
                    || 'Der Soll-vs-Ist-Verlauf konnte nicht geladen werden.';
            }
        });
    }

    /**
    * Number of whole months in the selected Zeitraum (inclusive), used as the `months` WINDOW for the
    * Ist trend endpoint (which has no from/to facet). Falls back to 12 when no range is set, and is
    * clamped to at least 1. Example: 2026-01..2026-06 → 6 months.
    */
    private rangeMonthCount(): number {
        const from = this.filterFromPeriod;
        const to = this.filterToPeriod;
        if (!from || !to) {
            return 12;
        }
        const [fy, fm] = from.split('-').map(Number);
        const [ty, tm] = to.split('-').map(Number);
        if (!fy || !fm || !ty || !tm) {
            return 12;
        }
        const count = (ty - fy) * 12 + (tm - fm) + 1;
        return count >= 1 ? count : 1;
    }

    /**
    * Merge the Soll and Ist monthly series into a single PrimeNG line-chart dataset, aligned by the
    * YYYY-MM month label. The labels are the sorted union of both series' months; a month absent from
    * one series reads 0 on that line. Plots Soll Gesamt (blue) vs Ist Gesamt (green) — the must-have
    * comparison. Returns null when neither series carries a month (the template renders the empty state).
    */
    private buildSollIstChartData(
        sollPoints: { month: string; gesamt: number }[],
        istPoints: CnActivityTrendPointDto[]
    ): any {
        if (sollPoints.length === 0 && istPoints.length === 0) {
            return null;
        }

        // Index each series by its YYYY-MM month for O(1) per-label lookup during alignment.
        const sollByMonth = new Map<string, number>();
        for (const p of sollPoints) {
            sollByMonth.set(p.month, p.gesamt ?? 0);
        }
        const istByMonth = new Map<string, number>();
        for (const p of istPoints) {
            istByMonth.set(p.period, p.gesamt ?? 0);
        }

        // Labels = sorted union of both series' months (YYYY-MM sorts lexicographically = chronologically).
        const labels = Array.from(new Set([...sollByMonth.keys(), ...istByMonth.keys()])).sort();

        // A month present in only one series contributes 0 to the missing line (spec).
        const sollData = labels.map(m => sollByMonth.get(m) ?? 0);
        const istData = labels.map(m => istByMonth.get(m) ?? 0);

        const documentStyle = getComputedStyle(document.documentElement);
        const blue = documentStyle.getPropertyValue('--blue-500') || '#3b82f6';
        const green = documentStyle.getPropertyValue('--green-500') || '#22c55e';

        return {
            labels,
            datasets: [
                {
                    label: 'Soll',
                    data: sollData,
                    borderColor: blue,
                    backgroundColor: blue,
                    tension: 0.3,
                    fill: false,
                    spanGaps: true
                },
                {
                    label: 'Ist',
                    data: istData,
                    borderColor: green,
                    backgroundColor: green,
                    tension: 0.3,
                    fill: false,
                    spanGaps: true
                }
            ]
        };
    }

    /**
    * Shared PrimeNG line-chart options for the Soll-vs-Ist chart, using CSS theme variables (text /
    * grid colours) so it follows the active theme. Mirrors the bd-kpi-trend conventions.
    */
    private buildSollIstChartOptions(): any {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color') || '#495057';
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#6c757d';
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#dee2e6';

        return {
            maintainAspectRatio: false,
            aspectRatio: 0.8,
            plugins: {
                legend: {labels: {color: textColor}}
            },
            scales: {
                x: {
                    ticks: {color: textColorSecondary},
                    grid: {color: surfaceBorder}
                },
                y: {
                    beginAtZero: true,
                    ticks: {color: textColorSecondary},
                    grid: {color: surfaceBorder}
                }
            }
        };
    }

    /**
    * Load the Quittierungsprotokoll — the audit list of acknowledged activities scoped by the
    * global filter PLUS the audit filters (Quittiert von / Quittiert am von–bis, Hans-Jürgen).
    *
    * The audit filters map to the already-supported GET /cn/activity params acknowledgedBy /
    * acknowledgedFrom / acknowledgedTo. The list endpoint scopes by partner server-side; the
    * Periode is applied client-side on bookingPeriod.
    *
    * Period-filter drift fix (Hans-Jürgen MINOR): when a Periode is set, keep ONLY rows whose
    * bookingPeriod equals the Periode (period-less rows are dropped), so the protocol uses the SAME
    * population as the period-scoped "Quittiert" controlling counter and the two agree.
    *
    * Default sort (Hans-Jürgen MINOR): by "Quittiert am" (acknowledgedAt) DESC — newest first —
    * instead of id DESC.
    */
    refreshProtocol(): void {
        this.protocolState = 'loading';
        this.protocolError = null;

        const filter: CnActivityFilterDto = {
            // B18 (TM-1000001915): the backend binds CommissionAcknowledgeStatus by enum NAME and the
            // numeric-enum reverse lookup proved unreliable in the optimized build, so pass the NAME
            // string directly (the same form the inbox uses) -> GET /cn/activity?acknowledgeStatus=ACKNOWLEDGED.
            acknowledgeStatus: 'ACKNOWLEDGED' as unknown as CnActivityFilterDto['acknowledgeStatus']
        };
        const partnerId = this.resolvePartnerId();
        if (partnerId !== undefined) {
            filter.partnerId = partnerId;
        }

        // Audit filters (Hans-Jürgen): Quittiert von + Quittiert am von–bis.
        const ackBy = this.protocolAckByFilter?.trim();
        if (ackBy) {
            filter.acknowledgedBy = ackBy;
        }
        const ackFrom = this.resolveAckFromIso();
        if (ackFrom) {
            filter.acknowledgedFrom = ackFrom;
        }
        const ackTo = this.resolveAckToIso();
        if (ackTo) {
            filter.acknowledgedTo = ackTo;
        }

        this.reconciliationApiService.listActivities(filter).subscribe({
            next: (entries) => {
                const period = this.resolvePeriod();
                // Period-filter drift fix: keep ONLY rows of the selected Periode (drop period-less
                // rows) so list + counter agree.
                const scoped = period
                    ? (entries ?? []).filter(e => e.bookingPeriod === period)
                    : (entries ?? []);
                this.protocolEntries = this.sortByAcknowledgedAtDesc(scoped);
                this.protocolState = this.protocolEntries.length ? 'ready' : 'empty';
            },
            error: (err) => {
                this.protocolState = 'error';
                this.protocolError = err?.message || 'Das Quittierungsprotokoll konnte nicht geladen werden.';
            }
        });
    }

    /**
    * Apply or reset the Quittierungsprotokoll audit filters — re-fetches only the protocol section.
    */
    onProtocolFilterChange(): void {
        this.refreshProtocol();
    }

    /** Reset the protocol audit filters back to "kein Filter". */
    resetProtocolFilter(): void {
        this.protocolAckByFilter = null;
        this.protocolAckFromDate = null;
        this.protocolAckToDate = null;
        this.refreshProtocol();
    }

    /** Whether any protocol audit filter is active (drives the reset button visibility). */
    hasActiveProtocolFilter(): boolean {
        return !!this.protocolAckByFilter?.trim()
            || this.protocolAckFromDate != null
            || this.protocolAckToDate != null;
    }

    /** "Quittiert am von" as an ISO Instant at day start, or undefined when unset. */
    private resolveAckFromIso(): string | undefined {
        if (!this.protocolAckFromDate) {
            return undefined;
        }
        const d = this.protocolAckFromDate;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0).toISOString();
    }

    /** "Quittiert am bis" as an ISO Instant at day end (inclusive), or undefined when unset. */
    private resolveAckToIso(): string | undefined {
        if (!this.protocolAckToDate) {
            return undefined;
        }
        const d = this.protocolAckToDate;
        return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999).toISOString();
    }

    /**
    * Sort acknowledged activities by acknowledgedAt DESC (newest first); rows without a timestamp
    * sort last. Returns a new array (does not mutate the input).
    */
    private sortByAcknowledgedAtDesc(entries: CommissionActivityDto[]): CommissionActivityDto[] {
        return [...entries].sort((a, b) => {
            const ta = a.acknowledgedAt ? new Date(a.acknowledgedAt).getTime() : Number.NEGATIVE_INFINITY;
            const tb = b.acknowledgedAt ? new Date(b.acknowledgedAt).getTime() : Number.NEGATIVE_INFINITY;
            return tb - ta;
        });
    }

    /**
    * Klartext label for a CommissionActivity kind (Art) — mirrors the inbox wording.
    */
    activityKindLabel(kind: string | CommissionActivityKindEnum | number | undefined): string {
        // B20 (TM-1000001915): the backend CnActivityDto serialises kind as the enum NAME string
        // ('NEW_COMMISSION' etc.), so map by name (mirrors the inbox kindLabel). Numeric values are
        // also tolerated for safety.
        switch (kind) {
            case 'NEW_COMMISSION':
            case CommissionActivityKindEnum.NEW_COMMISSION:
                return 'Neuvertrag';
            case 'STORNO':
            case CommissionActivityKindEnum.STORNO:
                return 'Storno';
            case 'RECONCILIATION':
            case CommissionActivityKindEnum.RECONCILIATION:
                return 'Abgleich';
            default:
                return 'Unbekannt';
        }
    }

    /**
    * Read the free-text Quittierungsnotiz from an activity row. The note is an optional backend
    * field (WP-BE-1) not yet typed on CommissionActivityDto, so it is read defensively.
    */
    activityNote(activity: CommissionActivityDto): string | null {
        const note = (activity as unknown as { note?: string }).note;
        return note && note.trim().length ? note : null;
    }

    /**
    * Single representative period as a YearMonth "YYYY-MM" string, or undefined.
    *
    * Used by the single-period consumers that cannot express a range: the inbox ([period]), the
    * Quittierungsprotokoll's client-side bookingPeriod filter, and the Datenqualität panel. Returns the
    * one month ONLY when the selected Zeitraum is a single month (from === to) — which is the default
    * "Dieser Monat" case, so those sections behave exactly as before; for a multi-month range it
    * returns undefined (no single-month equality filter applies).
    */
    resolvePeriod(): string | undefined {
        if (this.filterFromPeriod && this.filterToPeriod && this.filterFromPeriod === this.filterToPeriod) {
            return this.filterFromPeriod;
        }
        return undefined;
    }

    /** Range start period as a YearMonth "YYYY-MM" (fromPeriod query param), or undefined for alle Perioden. */
    resolveFromPeriod(): string | undefined {
        return this.filterFromPeriod ?? undefined;
    }

    /** Range end period as a YearMonth "YYYY-MM" (toPeriod query param), or undefined for alle Perioden. */
    resolveToPeriod(): string | undefined {
        return this.filterToPeriod ?? undefined;
    }

    /**
    * Take the year-month "YYYY-MM" of a range-selector boundary value. The overlay emits localDate
    * strings ("YYYY-MM-DD"); we slice the leading "YYYY-MM". Date objects are tolerated for safety.
    * Returns null for an empty/cleared value.
    */
    private formatYearMonth(value: string | Date | null | undefined): string | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        if (value instanceof Date) {
            const year = value.getFullYear();
            const month = String(value.getMonth() + 1).padStart(2, '0');
            return `${year}-${month}`;
        }
        const match = String(value).match(/^(\d{4})-(\d{2})/);
        return match ? `${match[1]}-${match[2]}` : null;
    }

    /** "MM.YYYY" of a "YYYY-MM" period, for the German Zeitraum label/description. */
    private formatPeriodForLabel(period: string): string {
        const [year, month] = period.split('-');
        return `${month}.${year}`;
    }

    /**
    * German label of the active Zeitraum derived from the from/to YearMonth values:
    * a single "MM.YYYY" for one month, "MM.YYYY–MM.YYYY" for a range, or "Alle Perioden" when unset.
    */
    private formatPeriodRangeLabel(): string {
        if (!this.filterFromPeriod || !this.filterToPeriod) {
            return 'Alle Perioden';
        }
        if (this.filterFromPeriod === this.filterToPeriod) {
            return this.formatPeriodForLabel(this.filterFromPeriod);
        }
        return `${this.formatPeriodForLabel(this.filterFromPeriod)}–${this.formatPeriodForLabel(this.filterToPeriod)}`;
    }

    /**
    * Current partner id, or undefined for alle Gesellschaften.
    * Public so the template can pass it into <mvs-cn-activity-inbox> [partnerId] (FE-1).
    */
    resolvePartnerId(): number | undefined {
        return this.filterPartnerId != null ? this.filterPartnerId : undefined;
    }

    // =====================================================================================
    // Attributed business AP+BP subtotal (Jean-Marie BLOCKER/MAJOR)
    // =====================================================================================

    /**
    * Attributed (zugeordnetes) Provisionsgeschäft = AP + BP, Offen-Spalte.
    * This is the headline figure of the real AP+BP business — shown ABOVE/stronger than the
    * Sonstige-dominated Gesamt row. Simple additive aggregation of two backend buckets (NOT a
    * derived business KPI), so summing the AP and BP offen amounts here is acceptable.
    */
    apBpOpen(): number {
        if (!this.totals) {
            return 0;
        }
        return (this.totals.ap.open ?? 0) + (this.totals.bp.open ?? 0);
    }

    /** Attributed Provisionsgeschäft = AP + BP, Quittiert-Spalte. */
    apBpAcknowledged(): number {
        if (!this.totals) {
            return 0;
        }
        return (this.totals.ap.acknowledged ?? 0) + (this.totals.bp.acknowledged ?? 0);
    }

    /** Attributed Provisionsgeschäft = AP + BP, Gesamt-Spalte (Offen + Quittiert). */
    apBpTotal(): number {
        return this.apBpOpen() + this.apBpAcknowledged();
    }

    // =====================================================================================
    // CSV export (FE-4)
    // =====================================================================================

    /**
    * Human-readable description of the active filter, written as the first line of every CSV export
    * so the recipient knows the scope (FE-4: "active filters in a header line").
    */
    private activeFilterDescription(): string {
        const partner = this.filterPartnerId != null
            ? (this.selectedPartner?.name ?? `Gesellschaft #${this.filterPartnerId}`)
            : 'Alle Gesellschaften';
        return `Zeitraum: ${this.activePeriodDescription()} · Gesellschaft: ${partner}`;
    }

    /**
    * Human-readable description of the active Zeitraum for the "Stand"/active-filter text and CSV
    * headers: the captured preset label (e.g. "Dieser Monat"), the derived "MM.YYYY–MM.YYYY" range,
    * or "Alle Perioden" when no Zeitraum is set.
    */
    activePeriodDescription(): string {
        if (!this.filterFromPeriod || !this.filterToPeriod) {
            return 'Alle Perioden';
        }
        // A named preset (Dieser Monat / Letzter Monat / Dieses Jahr / Letztes Jahr) reads nicer than the
        // raw month range; CUSTOM and unknown presets fall back to the derived "MM.YYYY[–MM.YYYY]" range.
        if (this.filterPeriodPreset !== null && this.filterPeriodPreset !== FilterDate.CUSTOM) {
            return this.filterDateLabels[this.filterPeriodPreset] ?? this.formatPeriodRangeLabel();
        }
        return this.formatPeriodRangeLabel();
    }

    /**
    * Filter description for the Quittierungsprotokoll CSV: the global scope PLUS the audit filters
    * (Quittiert von / Quittiert am von–bis, Hans-Jürgen) so the protocol export documents exactly
    * which audit query produced it.
    */
    private protocolFilterDescription(): string {
        const parts: string[] = [this.activeFilterDescription()];
        const ackBy = this.protocolAckByFilter?.trim();
        parts.push(`Quittiert von: ${ackBy ? ackBy : 'Alle'}`);
        const from = this.protocolAckFromDate ? this.formatGermanDate(this.protocolAckFromDate) : '–';
        const to = this.protocolAckToDate ? this.formatGermanDate(this.protocolAckToDate) : '–';
        parts.push(`Quittiert am: ${from} bis ${to}`);
        return parts.join(' · ');
    }

    /** Format a Date as German DD.MM.YYYY. */
    private formatGermanDate(date: Date): string {
        const pad = (n: number): string => String(n).padStart(2, '0');
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`;
    }

    /** Format a number as a German decimal string (e.g. 1234.5 → "1.234,50"); null → "–". */
    private formatGermanNumber(value: number | null | undefined, fractionDigits: number = 2): string {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '–';
        }
        return value.toLocaleString('de-DE', {
            minimumFractionDigits: fractionDigits,
            maximumFractionDigits: fractionDigits
        });
    }

    /** Format a percent value (e.g. 12.3 → "12,3 %"); null → "–". */
    private formatGermanPercent(value: number | null | undefined): string {
        if (value === null || value === undefined || Number.isNaN(value)) {
            return '–';
        }
        return `${value.toLocaleString('de-DE', {minimumFractionDigits: 0, maximumFractionDigits: 1})} %`;
    }

    /** Total (open + acknowledged) of a totals bucket — used only for CSV rows. */
    private bucketTotal(bucket: CnActivityTotalsBucketDto): number {
        return (bucket.open ?? 0) + (bucket.acknowledged ?? 0);
    }

    /**
    * Export the Provisionssummen matrix as CSV (FE-4). Uses the backend Gesamt bucket for the total
    * row (no FE recompute) and German number formatting.
    */
    exportTotalsCsv(): void {
        if (!this.totals) {
            return;
        }
        const headers = ['Filter', 'Provisionsart', 'Offen (EUR)', 'Quittiert (EUR)', 'Gesamt (EUR)'];
        const filterLine = this.activeFilterDescription();
        const bucketRow = (label: string, bucket: CnActivityTotalsBucketDto): string[] => [
            filterLine,
            label,
            this.formatGermanNumber(bucket.open),
            this.formatGermanNumber(bucket.acknowledged),
            this.formatGermanNumber(this.bucketTotal(bucket))
        ];
        const rows: string[][] = [
            bucketRow('Abschlussprovision (AP)', this.totals.ap),
            bucketRow('Bestandspflegeprovision (BP)', this.totals.bp),
            bucketRow('Sonstige', this.totals.sonstige),
            bucketRow('Gesamt', this.totals.gesamt)
        ];
        downloadCsv(headers, rows, 'provisionssummen.csv');
    }

    /**
    * Export the cross-Gesellschaft overview (Provisionen je Gesellschaft) as CSV (Jean-Marie).
    * One row per Gesellschaft (Gesellschaft · AP · BP · AP+BP · Gesamt) plus a Summen row. All EUR
    * figures come from the backend; the Summen row is a plain column total of the listed rows.
    */
    exportByPartnerCsv(): void {
        if (!this.byPartner.length) {
            return;
        }
        const headers = ['Filter', 'Gesellschaft', 'AP (EUR)', 'BP (EUR)', 'AP+BP (EUR)', 'Gesamt (EUR)'];
        const filterLine = this.activeFilterDescription();
        const rows: string[][] = this.byPartner.map(r => [
            filterLine,
            r.partnerName ?? '–',
            this.formatGermanNumber(r.ap),
            this.formatGermanNumber(r.bp),
            this.formatGermanNumber(r.apBp),
            this.formatGermanNumber(r.gesamt)
        ]);
        rows.push([
            filterLine,
            'Summe',
            this.formatGermanNumber(this.byPartnerSum('ap')),
            this.formatGermanNumber(this.byPartnerSum('bp')),
            this.formatGermanNumber(this.byPartnerSum('apBp')),
            this.formatGermanNumber(this.byPartnerSum('gesamt'))
        ]);
        downloadCsv(headers, rows, 'provisionen-je-gesellschaft.csv');
    }

    /**
    * Column total of the by-partner rows for the Summen line (template + CSV). Plain additive sum of
    * the listed Gesellschaften — not a derived business KPI.
    */
    byPartnerSum(field: 'ap' | 'bp' | 'apBp' | 'gesamt'): number {
        return this.byPartner.reduce((acc, r) => acc + (r[field] ?? 0), 0);
    }

    /**
    * Export the controlling KPIs as CSV (FE-4). One KPI per row (Kennzahl / Wert), German formatting.
    */
    exportControllingCsv(): void {
        if (!this.controlling) {
            return;
        }
        const headers = ['Filter', 'Kennzahl', 'Wert'];
        const filterLine = this.activeFilterDescription();
        const c = this.controlling;
        const ageSuffix = c.oldestOpenAgeDays != null ? ` (seit ${c.oldestOpenAgeDays} Tagen offen)` : '';
        const rows: string[][] = [
            [filterLine, 'Quittierungs-Quote', this.formatGermanPercent(c.completionRatePercent)],
            [filterLine, 'Offen', this.formatGermanNumber(c.openCount, 0)],
            [filterLine, 'Quittiert', this.formatGermanNumber(c.acknowledgedCount, 0)],
            [filterLine, 'Überfällig', this.formatGermanNumber(c.overdueCount, 0)],
            [filterLine, 'Heute quittiert', this.formatGermanNumber(c.throughputToday, 0)],
            [filterLine, 'Ältester offener Vorgang', `${c.oldestOpenPeriod ?? '–'}${ageSuffix}`]
        ];
        downloadCsv(headers, rows, 'controlling-kennzahlen.csv');
    }

    /**
    * Export the AP/BP trend as CSV (Jean-Marie MINOR). One row per Periode (Periode · AP · BP ·
    * Gesamt) plus a trailing "vs. Vormonat"-Delta row (EUR + %), consistent with the other exports.
    */
    exportTrendCsv(): void {
        if (!this.trend || !this.trend.series.length) {
            return;
        }
        const headers = ['Filter', 'Periode', 'AP (EUR)', 'BP (EUR)', 'Gesamt (EUR)'];
        const filterLine = this.activeFilterDescription();
        const rows: string[][] = this.trend.series.map(point => [
            filterLine,
            point.period,
            this.formatGermanNumber(point.ap),
            this.formatGermanNumber(point.bp),
            this.formatGermanNumber(point.gesamt)
        ]);
        const d = this.trend.delta;
        rows.push([
            filterLine,
            'vs. Vormonat (Delta)',
            `${this.formatGermanNumber(d.apDelta)} (${this.formatGermanPercent(d.apDeltaPercent)})`,
            `${this.formatGermanNumber(d.bpDelta)} (${this.formatGermanPercent(d.bpDeltaPercent)})`,
            `${this.formatGermanNumber(d.gesamtDelta)} (${this.formatGermanPercent(d.gesamtDeltaPercent)})`
        ]);
        downloadCsv(headers, rows, 'provisions-trend.csv');
    }

    /**
    * Export the Quittierungsprotokoll as CSV (FE-4). One acknowledged activity per row with the
    * active filters (global scope + audit filters) in the leading column, German formatting and
    * DD.MM.YYYY HH:mm timestamps.
    */
    exportProtocolCsv(): void {
        if (!this.protocolEntries.length) {
            return;
        }
        const headers = ['Filter', 'Gesellschaft', 'Art', 'Betrag (EUR)', 'Quittiert von', 'Quittiert am', 'Notiz'];
        const filterLine = this.protocolFilterDescription();
        const rows: string[][] = this.protocolEntries.map(entry => {
            const row = entry as unknown as {
                partnerName?: string; partner?: string; amount?: number | null; acknowledgedBy?: string;
                acknowledgedAt?: string; kind?: string | number;
            };
            return [
                filterLine,
                row.partnerName || row.partner || '–',
                this.activityKindLabel(row.kind),
                row.amount != null ? this.formatGermanNumber(row.amount) : '–',
                row.acknowledgedBy || '–',
                row.acknowledgedAt ? this.formatGermanDateTime(row.acknowledgedAt) : '–',
                this.activityNote(entry) ?? '–'
            ];
        });
        downloadCsv(headers, rows, 'quittierungsprotokoll.csv');
    }

    /** Format an ISO timestamp as German DD.MM.YYYY HH:mm; invalid/empty → "–". */
    private formatGermanDateTime(iso: string): string {
        const date = new Date(iso);
        if (Number.isNaN(date.getTime())) {
            return '–';
        }
        const pad = (n: number): string => String(n).padStart(2, '0');
        return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()} ` +
            `${pad(date.getHours())}:${pad(date.getMinutes())}`;
    }
}
